import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
import { formatEuro } from '../utils/currency';

interface RevenueOverviewCardProps {
  label: string;
  value: number;
  icon: LucideIcon;
  colorClass: string;
  change?: number;
  isPercentage?: boolean;
  loading?: boolean;
}

export function RevenueOverviewCard({
  label,
  value,
  icon: Icon,
  colorClass,
  change,
  isPercentage = false,
  loading = false,
}: RevenueOverviewCardProps) {
  const animatedValue = useCountUp({
    end: value,
    duration: 2000,
    decimals: isPercentage ? 1 : 0
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-12 w-12 bg-gray-200 rounded-xl mb-4"></div>
        <div className="h-4 w-32 bg-gray-200 rounded mb-3"></div>
        <div className="h-8 w-40 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const getColorClasses = () => {
    switch (colorClass) {
      case 'green':
        return {
          border: 'border-emerald-300',
          bg: 'bg-emerald-100',
          icon: 'text-emerald-600',
          text: 'text-emerald-600',
        };
      case 'blue':
        return {
          border: 'border-red-300',
          bg: 'bg-red-100',
          icon: 'text-red-600',
          text: 'text-red-600',
        };
      case 'purple':
        return {
          border: 'border-purple-300',
          bg: 'bg-purple-100',
          icon: 'text-purple-600',
          text: 'text-purple-600',
        };
      case 'emerald':
        return {
          border: 'border-blue-300',
          bg: 'bg-blue-100',
          icon: 'text-blue-600',
          text: 'text-blue-600',
        };
      default:
        return {
          border: 'border-gray-300',
          bg: 'bg-gray-100',
          icon: 'text-gray-600',
          text: 'text-gray-900',
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className={`bg-white rounded-xl border ${colors.border} p-6 hover:shadow-lg transition-all duration-300`}>
      <div className={`${colors.bg} rounded-xl p-3 inline-block mb-4`}>
        <Icon className={`w-6 h-6 ${colors.icon}`} />
      </div>

      <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">{label}</p>

      <div className="flex items-end justify-between">
        <p className={`text-2xl font-bold ${colors.text}`}>
          {isPercentage
            ? `${animatedValue.toFixed(1)}%`
            : formatEuro(animatedValue)}
        </p>

        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-semibold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-2">
        {colorClass === 'green' && 'Total recebido de clientes'}
        {colorClass === 'blue' && 'Total pago a influencers'}
        {colorClass === 'purple' && 'Total de comissões geradas'}
        {colorClass === 'emerald' && 'Lucro após custos de influencers'}
      </p>
    </div>
  );
}
